import { AnimatePresence, motion } from "motion/react";
import React from "react";
import { UI_MOTION } from "../../../config/uiMotion.configuration";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import {
    isUiMotionEnabled,
    resolveUiEaseOut,
} from "../../shared/motion/uiMotion.tools";
import { STOP_POPUP_ARRIVAL_ROW_SPACING_CLASS_NAME } from "./stopPopup.layout.constants";

interface StopPopupArrivalRowMotionProps {

    /** Arrival chip content. */
    "children": React.ReactNode;

    /** Chip shell classes. */
    "className": string;

    /** Chip shell inline styles. */
    "style": React.CSSProperties;
}

/**
 * Animated arrival row for the stop popup list.
 *
 * A layout shell wraps the static chip: at rest the shell sizes from content (no animated
 * height). On exit the shell collapses while siblings reflow instantly (no layout projection).
 *
 * @param props Row motion props.
 * @returns Animated row shell or static chip when motion is disabled.
 */
const StopPopupArrivalRowMotion: React.FC<StopPopupArrivalRowMotionProps> = ( {
    children,
    className,
    style,
} ) => {
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    if ( !animationsEnabled ) {
        return (
            <div
                className={`${ className } ${ STOP_POPUP_ARRIVAL_ROW_SPACING_CLASS_NAME }`}
                style={style}
            >
                {children}
            </div>
        );
    }

    const departureExit = resolveUiEaseOut( reducedMotion, UI_MOTION.duration.stopPopupDepartureExitMs );

    return (
        <motion.div
            animate={{ "opacity": 1 }}
            className={STOP_POPUP_ARRIVAL_ROW_SPACING_CLASS_NAME}
            exit={{
                "height":       0,
                "marginBottom": 0,
                "opacity":      0,
            }}
            initial={false}
            style={{ "overflow": "hidden" }}
            transition={{
                "height":       departureExit,
                "marginBottom": departureExit,
                "opacity":      departureExit,
            }}
        >
            <div
                className={className}
                style={style}
            >
                {children}
            </div>
        </motion.div>
    );
};

interface StopPopupArrivalListPresenceProps {

    /** Keyed {@link StopPopupArrivalRowMotion} rows. */
    "children": React.ReactNode;
}

/**
 * Enables exit + sibling reflow for stop popup arrival rows when UI motion is on.
 *
 * @param props Presence wrapper props.
 * @returns Layout group + AnimatePresence shell or passthrough fragment.
 */
export const StopPopupArrivalListPresence: React.FC<StopPopupArrivalListPresenceProps> = ( { children } ) => {
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    if ( !animationsEnabled ) {
        return children;
    }

    return (
        <AnimatePresence initial={false}>
            {children}
        </AnimatePresence>
    );
};

export default StopPopupArrivalRowMotion;
